import React, { useEffect, useState, useRef } from 'react';
import FormInput from './FormInput';
import { useForm } from 'react-hook-form';
import { useSidebar } from '../../contexts/SidebarContext';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const DashboardFilters = ({
  onFiltersChange,
  onLoadingChange,
  top = '0',
  useRelativePosition = false,
  initialProjectID = null,
  initialMonth = null,
  selectAllByDefault = false,
  rightContent = null,
  projectCountsById = null,
  // Marketing filters by year, not month, so it takes the bar without the picker.
  showMonth = true
}) => {
  const {
    register,
    formState: { errors },
    setValue,
    watch
  } = useForm({
    defaultValues: {
      project: initialProjectID || '',
      month: initialMonth || ''
    }
  });
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const { collapsed } = useSidebar();
  const userID = Number(Cookies.get('userId'));
  const projectID = watch('project');
  const [month, setMonth] = useState('');
  const prevFiltersRef = useRef({ projectID: null, month: null });

  // Add "All" option to dropdown if selectAllByDefault is true
  const baseDropdownOptions = selectAllByDefault
    ? [{ id: '', value: '', label: 'All' }, ...(projectsForDropdown || [])]
    : projectsForDropdown || [];

  const dropdownOptions = projectCountsById
    ? baseDropdownOptions.map((opt) => {
      const count = projectCountsById?.[String(opt?.id)];
      if (count === undefined) return opt;
      return {
        ...opt,
        label: `${opt.label} (${count})`
      };
    })
    : baseDropdownOptions;

  // Initialize month to current month
  useEffect(() => {
    if (initialMonth) {
      setMonth(initialMonth);
    } else {
      const today = new Date();
      const initialMonth = today.getMonth() + 1;
      setMonth(initialMonth);
    }
  }, [initialMonth]);

  // Auto-select first project when projects are loaded (or "All" if selectAllByDefault is true)
  useEffect(() => {
    if (projects && projects.length > 0) {
      if (initialProjectID) {
        setValue('project', initialProjectID);
      } else if (selectAllByDefault) {
        // Select "All" option (empty string) instead of first project
        setValue('project', '');
      } else {
        setValue('project', projects[0]?.id);

        // Track project auto-selection
        mixpanel.track('Dashboard Project Auto Selected', {
          user_id: userID,
          project_id: projects[0]?.id,
          project_name: projects[0]?.name,
          timestamp: new Date().toISOString()
        });
      }
    }
  }, [projects, setValue, userID, initialProjectID, selectAllByDefault]);

  // Notify parent when filters change (only if values actually changed)
  useEffect(() => {
    if (
      projectID !== undefined &&
      projectID !== null &&
      (month || !showMonth) &&
      onFiltersChange &&
      (prevFiltersRef.current.projectID !== projectID || prevFiltersRef.current.month !== month)
    ) {
      prevFiltersRef.current = { projectID, month };
      onFiltersChange({ projectID, month });
    }
  }, [projectID, month, showMonth, onFiltersChange]);

  // Notify parent about loading state
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isProjectsLoading);
    }
  }, [isProjectsLoading, onLoadingChange]);

  const handleMonthChange = (value) => {
    // value is 1–12 as passed from the dropdown
    setMonth(value);

    // Track month change
    mixpanel.track('Dashboard Month Changed', {
      user_id: userID,
      month: value,
      project_id: projectID,
      timestamp: new Date().toISOString()
    });
  };

  const handleProjectChange = (selected) => {
    // Track project change
    mixpanel.track('Dashboard Project Changed', {
      user_id: userID,
      previous_project_id: projectID,
      new_project_id: selected?.id,
      new_project_name: selected?.label,
      timestamp: new Date().toISOString()
    });
  };

  const getTopStyle = () => {
    if (!top || top === '0') return '0';
    if (typeof top === 'string') {
      if (top.includes('px') || top.includes('rem') || top.includes('%') || top.includes('vh')) {
        return top;
      }
      return `${top}px`;
    }
    return `${top}px`;
  };

  const positionClass = useRelativePosition ? 'relative' : 'fixed';
  const positionStyles = useRelativePosition
    ? {}
    : {
        top: getTopStyle(),
        left: collapsed ? '76px' : '16rem',
        right: '0'
      };

  return (
    <div
      className={`flex justify-between items-center gap-2 ${positionClass} bg-white border px-6 py-3 pb-6 z-30 transition-all duration-300 ease-in-out ${
        !useRelativePosition && (collapsed ? 'left-[76px] right-0' : 'left-64 right-0')
      }`}
      style={positionStyles}>
      <div
        className={`flex gap-3 w-[50%] justify-start items-center transition-all duration-300 ease-in-out`}>
        <FormInput
          width={50}
          label={''}
          register={register}
          register_key={'project'}
          isRequired={false}
          type={'dropdown'}
          options={dropdownOptions}
          validation={{ required: 'Project is required' }}
          errors={errors}
          onType={handleProjectChange}
        />
        {showMonth && (
          <FormInput
            width={26}
            register={register}
            register_key={'month'}
            label={''}
            type={'date-month'}
            isRequired={false}
            onType={(mo) => {
              handleMonthChange(mo.id);
            }}
            valuePre={month}
          />
        )}
      </div>
      {rightContent}
    </div>
  );
};

export default DashboardFilters;
